import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';

const getPaidInclusion = state => state.paidInclusion;

// Error response could either coming from rm-api or metroid backend.
// Metroid's error response respects { code, code_details, message }.
const PaidInclusionSelectors = {
    makeApiAccessToken: state => ({
        capture: getPaidInclusion(state).capture.apiAccessToken,
        charge: getPaidInclusion(state).charge.apiAccessToken,
    }),
    makeIsFormLoading: state => getPaidInclusion(state).capture.isFormLoading,
    makeIsFormLoaded: state => getPaidInclusion(state).capture.isFormLoaded,
    makeIsCapturing: state => getPaidInclusion(state).capture.isCapturing,
    makeIsCaptured: state => getPaidInclusion(state).capture.isCaptured,
    makeIsCharging: state => getPaidInclusion(state).charge.isCharging,
    makeIsCharged: state => getPaidInclusion(state).charge.isCharged,
    makeIsPaymentValid: state => getPaidInclusion(state).verifyPaymentAndActivateListing.isPaymentValid,
    makeDefaultPaymentMethodSuccess: state => getPaidInclusion(state).verifyPaymentAndActivateListing
        .defaultPaymentMethodSuccess,
    makeDefaultPaymentMethod: state => getPaidInclusion(state).verifyPaymentAndActivateListing.defaultPaymentMethod,
    makeIsPaymentSummaryDefaultPaymentMethodSuccess: state => getPaidInclusion(state).verifyPaymentAndActivateListing
        .isPaymentSummaryDefaultPaymentMethodSuccess,
    makePrice: state => getPaidInclusion(state).details.price,
    makeIsEligibleForLeadGuarantee: state => getPaidInclusion(state).details.isEligibleForLeadGuarantee,
    makeHasError: (state) => {
        const {
            capture, charge, verifyPaymentAndActivateListing, getPrice,
        } = getPaidInclusion(state);
        return !!(
            capture.error || charge.error || getPrice.error || verifyPaymentAndActivateListing.paymentSummaryFailed ||
            PaidInclusionSelectors.makePaymentFailErrorMessage(state) ||
            PaidInclusionSelectors.makePaymentPendingErrorMessage(state)
        );
    },
    makeListingCannotBeActivatedError: (state) => {
        const { capture, charge } = getPaidInclusion(state);
        if (capture.listingCannotBeActivated) {
            return capture.error;
        }
        if (charge.listingCannotBeActivated) {
            return charge.error;
        }
        return null;
    },
    makeFraudErrorMessage: (state) => {
        const { capture, charge } = getPaidInclusion(state);
        const { error: captureError } = capture;
        if (captureError && captureError.data && captureError.data.isPaymentAllowed === false) {
            return captureError.message;
        }
        const { error: chargeError } = charge;
        if (chargeError && chargeError.data && chargeError.data.isPaymentAllowed === false) {
            return chargeError.message;
        }
        return '';
    },
    makeInitiateCardCaptureErrorMessage: (state) => {
        const { error } = getPaidInclusion(state).capture;
        if (!error) {
            return '';
        }
        switch (error.code) {
            case PaidInclusionConstants.CAPTURE.INITIATE.ERROR.METROID_MAINTENANCE_MODE:
                return PaidInclusionConstants.ERROR_TYPE.METROID_MAINTENANCE_MODE;
            case PaidInclusionConstants.CAPTURE.INITIATE.ERROR.CANNOT_MAKE_PAYMENTS:
                return PaidInclusionConstants.ERROR_TYPE.CANNOT_MAKE_PAYMENTS;
            default:
                return '';
        }
    },
    makeVerifyPaymentAndActivateListingErrorMessage: (state) => {
        const { errorCode } = getPaidInclusion(state).verifyPaymentAndActivateListing;
        if (errorCode === PaidInclusionConstants.CAPTURE.INITIATE.ERROR.METROID_MAINTENANCE_MODE) {
            return PaidInclusionConstants.ERROR_TYPE.METROID_MAINTENANCE_MODE;
        }
        return '';
    },
    makeFormErrorMessage: (state) => {
        const { error } = getPaidInclusion(state).capture;
        if (error && error.code === 'ZE-UI') {
            if (error.code_details.includes(PaidInclusionConstants.ERROR_TYPE.EXPIRATION_PAST)) {
                return PaidInclusionConstants.ERROR_TYPE.EXPIRATION_PAST;
            }
            if (error.code_details.includes('ZIP code')) {
                return PaidInclusionConstants.ERROR_TYPE.BAD_ZIP;
            }
            if (error.code_details.includes('Invalid CC Number')) {
                return PaidInclusionConstants.ERROR_TYPE.INVALID_CARD_NUMBER;
            }
        }
        return '';
    },
    makePaymentFailErrorMessage: (state) => {
        const { error } = getPaidInclusion(state).charge;
        if (error) {
            switch (error.code) {
                case PaidInclusionConstants.CHARGE.INITIATE.ERROR.METROID_MAINTENANCE_MODE:
                    return PaidInclusionConstants.ERROR_TYPE.METROID_MAINTENANCE_MODE;
                case PaidInclusionConstants.CHARGE.INITIATE.ERROR.CANNOT_MAKE_PAYMENTS:
                    return PaidInclusionConstants.ERROR_TYPE.CANNOT_MAKE_PAYMENTS;
                case 'CHRF-F':
                case 'CHRG-TO':
                    return PaidInclusionConstants.ERROR_TYPE.PAYMENT_FAILURE;
                default:
                    break;
            }
        }

        const { paymentStatus, isPostCharge } = getPaidInclusion(state).verifyPaymentAndActivateListing;
        if (isPostCharge && paymentStatus === PaidInclusionConstants.PAYMENT_STATUS.INVALID) {
            return PaidInclusionConstants.ERROR_TYPE.PAYMENT_FAILURE;
        }
        return '';
    },
    makeIsInitiateChargeFailedValidPaymentExists: (state) => {
        const { validPaymentExists } = getPaidInclusion(state).charge;
        return validPaymentExists;
    },
    makePaymentPendingErrorMessage: (state) => {
        const { isPaymentPending, validPaymentExists } = getPaidInclusion(state).charge;
        if (isPaymentPending) {
            return PaidInclusionConstants.ERROR_TYPE.PAYMENT_PENDING;
        }

        const { paymentStatus, isPostCharge } = getPaidInclusion(state).verifyPaymentAndActivateListing;
        if (isPostCharge && paymentStatus === PaidInclusionConstants.PAYMENT_STATUS.PENDING) {
            if (validPaymentExists) {
                return PaidInclusionConstants.ERROR_TYPE.DEFAULT;
            }
            return PaidInclusionConstants.ERROR_TYPE.PAYMENT_PENDING;
        }
        return '';
    },
};

export default PaidInclusionSelectors;
